import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, MessageCircle, Upload, Search } from "lucide-react";
import { WaImportCard } from "@/components/whatsapp/WaImportCard";
import { WaCloudCard } from "@/components/whatsapp/WaCloudCard";
import { WaCostsTab } from "@/components/whatsapp/WaCostsTab";
import { WaTemplatesTab } from "@/components/whatsapp/WaTemplatesTab";
import { WaSegmentsTab } from "@/components/whatsapp/WaSegmentsTab";
import { WaCampaignsTab } from "@/components/whatsapp/WaCampaignsTab";
import { WaContactsCard } from "@/components/whatsapp/WaContactsCard";
import { WaReportsTab, WaLinkDomainsCard } from "@/components/whatsapp/WaReportsTab";
import { WaConnectionStatus } from "@/components/whatsapp/WaConnectionStatus";

const DATA_TABS = [["overview", "Overview"], ["costs", "Costs"]];
const MAIN_TABS = [["templates", "Templates"], ["lists", "Customer lists"], ["campaigns", "Campaigns"], ["reports", "Reports"]];

const FIELDS = [
  ["waba_id", "WABA ID"], ["phone_number_id", "Phone Number ID"], ["display_number", "WhatsApp number"],
  ["display_name", "Display name"], ["business_status", "Business status"], ["phone_status", "Phone status"],
  ["quality_rating", "Quality rating"], ["messaging_limit", "Messaging limit"], ["partners", "Partners on WABA"],
  ["payment_provider", "Meta payment provider"], ["platform_type", "Number platform"], ["notes", "Notes"],
];
const errText = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : d?.code || "Request failed"; };

function AccountCard({ st, canEdit, onSaved }) {
  const [f, setF] = useState(st.account || {});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/wa/account", f); toast.success("Account details saved"); onSaved(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="wa-account-card">
      <h2 className="text-sm font-bold text-slate-900 mb-1">WhatsApp Business Account</h2>
      <p className="text-xs text-slate-500 mb-4">Reference details only. Nothing here connects to Meta.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label]) => (
          <label key={k} className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
            <Input value={f[k] || ""} disabled={!canEdit} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`wa-account-${k}`} />
          </label>
        ))}
      </div>
      {canEdit && <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={save} data-testid="wa-account-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save details</Button>}
    </section>
  );
}

function ConsentLookup() {
  const [n, setN] = useState("");
  const [r, setR] = useState(null);
  const look = async () => { try { setR((await api.get("/wa/consents", { params: { number: n } })).data); } catch (e) { toast.error(errText(e)); } };
  const tone = { granted: "text-emerald-600", withdrawn: "text-rose-600", unevidenced: "text-amber-600", unknown: "text-slate-500" };
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="wa-consent-lookup">
      <h2 className="text-sm font-bold text-slate-900 mb-3">Consent lookup</h2>
      <div className="flex gap-2">
        <Input placeholder="+44 7…" value={n} onChange={(e) => setN(e.target.value)} data-testid="wa-consent-number" />
        <Button variant="outline" onClick={look} data-testid="wa-consent-search"><Search className="w-4 h-4" /></Button>
      </div>
      {r && (
        <div className="mt-3 text-sm" data-testid="wa-consent-result">
          <div>Marketing consent: <b className={tone[r.status]} data-testid="wa-consent-status">{r.status}</b></div>
          <ul className="mt-2 space-y-1 text-xs text-slate-500">
            {r.events.map((e) => <li key={e.id}>{e.kind} · {e.occurred_at ? new Date(e.occurred_at).toLocaleString("en-GB") : "date unknown"} · {e.source}{e.imported_from ? ` · imported from ${e.imported_from}` : ""}</li>)}
          </ul>
        </div>
      )}
    </section>
  );
}

export default function WhatsAppSettings({ section }) {
  const { can } = useAuth();
  const [st, setSt] = useState(null);
  const [err, setErr] = useState(null);
  const TABS = section === "data" ? DATA_TABS : MAIN_TABS;
  const [tab, setTab] = useState(TABS[0][0]);
  const [cloud, setCloud] = useState(null);
  const load = () => api.get("/wa/status").then((r) => setSt(r.data)).catch((e) => setErr(errText(e)));
  useEffect(() => { load(); api.get("/wa/cloud/status").then((r) => setCloud(r.data)).catch(() => {}); }, []);
  if (err) return <div className="p-8 text-sm text-slate-500" data-testid="wa-disabled">{err}</div>;
  if (!st) return <div className="p-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8 max-w-5xl space-y-5" data-testid="wa-settings-page">
      <div className="flex items-center gap-3">
        {section === "data" ? <MessageCircle className="w-6 h-6 text-emerald-600" /> : <PageIcon />}
        <div><h1 className="text-2xl font-bold text-slate-900">{section === "data" ? "WhatsApp Setup" : "WhatsApp"}</h1><p className="text-sm text-slate-500" data-testid="wa-subtitle">{cloud?.connected ? `Meta Cloud API · connected · sending ${cloud.live_sending ? (cloud.send_scope === "all" ? "enabled" : "approved test recipients only") : "locked"}` : "Meta Cloud API · not connected"}</p></div>
      </div>
      <WaConnectionStatus cloud={cloud} />
      <div className="flex flex-wrap gap-1 border-b border-slate-200" data-testid="wa-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`wa-tab-${k}`}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{label}</button>
        ))}
      </div>
      {tab === "costs" && <WaCostsTab canEdit={can("whatsapp.settings.manage")} />}
      {tab === "lists" && <WaSegmentsTab />}
      {tab === "campaigns" && <WaCampaignsTab simulator={!!st.simulator} />}
      {tab === "reports" && <WaReportsTab />}
      {tab === "templates" && <WaTemplatesTab />}
      {tab === "overview" && <>
      <AccountCard st={st} canEdit={can("whatsapp.settings.manage")} onSaved={load} />
      <WaCloudCard />
      <WaLinkDomainsCard canEdit={can("whatsapp.settings.manage")} />
      {can("whatsapp.consent.manage") && <WaImportCard onDone={load} icon={Upload} />}
      {can("whatsapp.consent.manage") && <WaContactsCard />}
      <ConsentLookup />
      </>}
    </div>
  );
}
